import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, ArrowDownToLine, RotateCcw, Trash2, FileText, QrCode, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function MobileWarehouseWODetailPage() {
  const { wo_id } = useParams();
  const navigate = useNavigate();
  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Sheet states
  const [activeSheet, setActiveSheet] = useState(null); // 'pick','return','waste','log'
  const [activeComponent, setActiveComponent] = useState(null);
  const [expandedBOM, setExpandedBOM] = useState({});

  // Form states
  const [pickForm, setPickForm] = useState({ lot_number: '', quantity: '', lp_number: '' });
  const [lpInfo, setLpInfo] = useState(null);
  const [returnForm, setReturnForm] = useState({ lot_number: '', quantity: '', reason: 'unused' });
  const [wasteForm, setWasteForm] = useState({ lot_number: '', quantity: '', waste_reason: 'floor_waste', notes: '' });

  const fetchWO = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/work-orders/${wo_id}`);
      setWo(res.data);
    } catch (err) {
      console.error('Failed to fetch WO:', err);
    } finally {
      setLoading(false);
    }
  }, [wo_id]);

  useEffect(() => {
    fetchWO();
    const interval = setInterval(fetchWO, 15000);
    return () => clearInterval(interval);
  }, [fetchWO]);

  // ====== ACTIONS ======

  const handleLogPallet = async () => {
    const input = prompt(`Enter cases per pallet for ${wo?.item_name}:`, '50');
    if (!input) return;
    const cases = parseInt(input);
    if (isNaN(cases) || cases <= 0) { toast.error('Enter a valid number'); return; }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/production/${wo_id}/log-pallet`, { cases });
      toast.success(`Pallet #${res.data.pallets_completed} logged: ${cases} cases`);
      fetchWO();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log pallet');
    } finally { setActionLoading(false); }
  };

  // Scan LP from pick sheet
  const lookupLP = async () => {
    if (!pickForm.lp_number.trim()) return;
    try {
      const res = await axios.get(`${API}/warehouse/license-plates/${pickForm.lp_number.trim()}`);
      setLpInfo(res.data);
      // Auto-fill lot number and quantity from LP
      setPickForm(f => ({
        ...f,
        lot_number: res.data.lot_number || f.lot_number,
        quantity: String(res.data.quantity || res.data.quantity_on_hand || ''),
      }));
      toast.success(`Found: ${res.data.item_name || res.data.lp_number}`);
    } catch {
      toast.error('LP not found');
      setLpInfo(null);
    }
  };

  const handlePick = async () => {
    if (!activeComponent || !pickForm.lot_number.trim() || !pickForm.quantity) {
      toast.error('Fill in lot number and quantity'); return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/production/${wo_id}/log-pick`, null, {
        params: {
          component_id: activeComponent.component_id,
          lot_number: pickForm.lot_number.trim(),
          quantity: parseFloat(pickForm.quantity),
          picked_by: 'warehouse_mobile'
        }
      });
      toast.success(`Picked ${pickForm.quantity} ${activeComponent.unit} of ${activeComponent.component_name}`);
      closeSheet();
      fetchWO();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log pick');
    } finally { setActionLoading(false); }
  };

  const handleReturn = async () => {
    if (!activeComponent || !returnForm.lot_number.trim() || !returnForm.quantity) {
      toast.error('Fill in lot number and quantity'); return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/production/${wo_id}/log-return`, null, {
        params: {
          component_id: activeComponent.component_id,
          lot_number: returnForm.lot_number.trim(),
          quantity: parseFloat(returnForm.quantity),
          returned_by: 'warehouse_mobile',
          reason: returnForm.reason || 'unused'
        }
      });
      toast.success(`Returned ${returnForm.quantity} ${activeComponent.unit}`);
      closeSheet();
      fetchWO();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log return');
    } finally { setActionLoading(false); }
  };

  const handleWaste = async () => {
    if (!activeComponent || !wasteForm.lot_number.trim() || !wasteForm.quantity) {
      toast.error('Fill in lot number and quantity'); return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/production/${wo_id}/log-waste`, null, {
        params: {
          component_id: activeComponent.component_id,
          lot_number: wasteForm.lot_number.trim(),
          quantity: parseFloat(wasteForm.quantity),
          waste_reason: wasteForm.waste_reason,
          logged_by: 'warehouse_mobile',
          notes: wasteForm.notes
        }
      });
      toast.success(`Waste logged: ${wasteForm.quantity} ${activeComponent.unit}`);
      closeSheet();
      fetchWO();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log waste');
    } finally { setActionLoading(false); }
  };

  const closeSheet = () => {
    setActiveSheet(null);
    setActiveComponent(null);
    setPickForm({ lot_number: '', quantity: '', lp_number: '' });
    setLpInfo(null);
    setReturnForm({ lot_number: '', quantity: '', reason: 'unused' });
    setWasteForm({ lot_number: '', quantity: '', waste_reason: 'floor_waste', notes: '' });
  };

  const openSheet = (type, comp) => {
    setActiveSheet(type);
    setActiveComponent(comp);
  };

  const toggleBOM = (compId) => {
    setExpandedBOM(prev => ({ ...prev, [compId]: !prev[compId] }));
  };

  // ====== HELPERS ======

  const getPickedTotal = (comp) => {
    let total = 0;
    (comp.lots || []).forEach(lot => {
      (lot.picks || []).forEach(p => { total += p.picked_qty || 0; });
    });
    return total;
  };

  const getReturnedTotal = (comp) => {
    let total = 0;
    (comp.lots || []).forEach(lot => {
      (lot.returns || []).forEach(r => { total += r.returned_qty || 0; });
    });
    return total;
  };

  const getWasteTotal = (comp) => {
    let total = 0;
    (comp.lots || []).forEach(lot => {
      (lot.waste || []).forEach(w => { total += w.waste_qty || 0; });
    });
    return total;
  };

  const getNeeded = (comp) => {
    return comp.planned_qty || ((comp.quantity_per || 0) * (wo?.planned_quantity || 0)) || 0;
  };

  const getYield = (comp) => {
    const picked = getPickedTotal(comp);
    const returned = getReturnedTotal(comp);
    const wasted = getWasteTotal(comp);
    const consumed = picked - returned - wasted;
    const needed = getNeeded(comp);
    if (needed <= 0 || consumed <= 0) return null;
    return ((needed / consumed) * 100);
  };

  const getAllLogs = (comp) => {
    const logs = [];
    (comp.lots || []).forEach(lot => {
      (lot.picks || []).forEach(p => {
        logs.push({ type: 'pick', qty: p.picked_qty, lot: p.lot_number || lot.lot_number, by: p.picked_by, at: p.picked_at, unit: comp.unit });
      });
      (lot.returns || []).forEach(r => {
        logs.push({ type: 'return', qty: r.returned_qty, lot: r.lot_number || lot.lot_number, by: r.returned_by, at: r.returned_at, reason: r.reason, unit: comp.unit });
      });
      (lot.waste || []).forEach(w => {
        logs.push({ type: 'waste', qty: w.waste_qty, lot: w.lot_number || lot.lot_number, by: w.logged_by, at: w.logged_at, reason: w.waste_reason, unit: comp.unit });
      });
    });
    return logs.sort((a, b) => (b.at || '').localeCompare(a.at || ''));
  };

  const yieldColor = (yld) => {
    if (!yld) return '#8e8e93';
    if (yld >= 97.5) return '#34c759';
    if (yld >= 90) return '#ff9500';
    return '#ff3b30';
  };

  // ====== RENDER ======

  if (loading) return <div className="mobile-page"><div className="mobile-loading"><div className="mobile-spinner" /></div></div>;

  if (!wo) return (
    <div className="mobile-page">
      <button className="mobile-back-btn" onClick={() => navigate(-1)}><ArrowLeft size={18} /> Back</button>
      <div className="mobile-empty-state"><p>Work order not found</p></div>
    </div>
  );

  const progress = wo.planned_quantity > 0 ? ((wo.actual_quantity || 0) / wo.planned_quantity) * 100 : 0;

  return (
    <div className="mobile-page">
      <button className="mobile-back-btn" onClick={() => navigate('/mobile/warehouse/work-orders')}>
        <ArrowLeft size={18} /> Work Orders
      </button>

      {/* WO Header */}
      <div className="mobile-detail-section" style={{ paddingBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 12, color: '#8e8e93' }}>{wo.wo_number} · Line {wo.production_line}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1c1c1e' }}>{wo.item_name}</div>
          </div>
          <span className={`mobile-status-badge ${wo.status}`}>
            {wo.status === 'in_production' ? 'Running' : wo.status === 'staged' ? 'Staged' : wo.status}
          </span>
        </div>

        <div className="mobile-kpi-row" style={{ marginBottom: 8 }}>
          <div className="mobile-kpi-card blue" style={{ padding: 10 }}>
            <div className="mobile-kpi-label" style={{ fontSize: 10 }}>Planned</div>
            <div className="mobile-kpi-value" style={{ fontSize: 20 }}>{wo.planned_quantity}</div>
          </div>
          <div className="mobile-kpi-card green" style={{ padding: 10 }}>
            <div className="mobile-kpi-label" style={{ fontSize: 10 }}>Actual</div>
            <div className="mobile-kpi-value" style={{ fontSize: 20 }}>{wo.actual_quantity || 0}</div>
          </div>
          <div className="mobile-kpi-card orange" style={{ padding: 10 }}>
            <div className="mobile-kpi-label" style={{ fontSize: 10 }}>Pallets</div>
            <div className="mobile-kpi-value" style={{ fontSize: 20 }}>{wo.pallets_completed || 0}</div>
          </div>
          <div className="mobile-kpi-card purple" style={{ padding: 10 }}>
            <div className="mobile-kpi-label" style={{ fontSize: 10 }}>Progress</div>
            <div className="mobile-kpi-value" style={{ fontSize: 20 }}>{progress.toFixed(0)}%</div>
          </div>
        </div>

        <div className="mobile-wo-progress">
          <div className="mobile-wo-progress-bar" style={{ height: 8 }}>
            <div className={`mobile-wo-progress-fill ${progress >= 100 ? 'green' : progress > 50 ? 'blue' : 'orange'}`} style={{ width: `${Math.min(progress, 100)}%` }} />
          </div>
        </div>
      </div>

      {/* Log Pallet */}
      {wo.status === 'in_production' && (
        <button className="mobile-action-btn primary" onClick={handleLogPallet} disabled={actionLoading} style={{ marginBottom: 16 }}>
          <Package size={18} /> Log Pallet
        </button>
      )}

      {/* ====== BOM SECTION ====== */}
      <div className="mobile-section-header">
        <span className="mobile-section-title">Bill of Materials</span>
        <span style={{ fontSize: 13, color: '#8e8e93' }}>{(wo.bom_usage || []).length} items</span>
      </div>

      {(wo.bom_usage || []).map((comp, idx) => {
        const picked = getPickedTotal(comp);
        const returned = getReturnedTotal(comp);
        const wasted = getWasteTotal(comp);
        const net = picked - returned;
        const needed = getNeeded(comp);
        const yld = getYield(comp);
        const isExpanded = expandedBOM[comp.component_id];
        const logs = getAllLogs(comp);
        const hasPicks = picked > 0;
        const fillPct = needed > 0 ? Math.min((net / needed) * 100, 100) : 0;

        return (
          <div key={comp.component_id || idx} style={{ background: 'white', borderRadius: 14, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            {/* Component Header */}
            <button
              onClick={() => toggleBOM(comp.component_id)}
              style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', padding: 16, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ flex: 1, marginRight: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1c1c1e', marginBottom: 4 }}>{comp.component_name}</div>
                <div style={{ fontSize: 12, color: '#8e8e93' }}>
                  {comp.component_type || comp.item_type || 'ingredient'} · {comp.quantity_per || '-'} {comp.unit}/case
                </div>
              </div>
              {isExpanded ? <ChevronUp size={20} style={{ color: '#8e8e93', flexShrink: 0 }} /> : <ChevronDown size={20} style={{ color: '#8e8e93', flexShrink: 0 }} />}
            </button>

            {/* Stats Row - always visible */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, padding: '0 16px 12px' }}>
              <div style={{ textAlign: 'center', padding: '8px 4px', background: '#f0fdf4', borderRadius: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#34c759' }}>{picked > 0 ? picked.toFixed(1) : '—'}</div>
                <div style={{ fontSize: 9, color: '#636366', fontWeight: 600, textTransform: 'uppercase' }}>Picked</div>
              </div>
              <div style={{ textAlign: 'center', padding: '8px 4px', background: '#fff7ed', borderRadius: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#ff9500' }}>{returned > 0 ? returned.toFixed(1) : '—'}</div>
                <div style={{ fontSize: 9, color: '#636366', fontWeight: 600, textTransform: 'uppercase' }}>Returned</div>
              </div>
              <div style={{ textAlign: 'center', padding: '8px 4px', background: '#fef2f2', borderRadius: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#ff3b30' }}>{wasted > 0 ? wasted.toFixed(1) : '—'}</div>
                <div style={{ fontSize: 9, color: '#636366', fontWeight: 600, textTransform: 'uppercase' }}>Waste</div>
              </div>
              <div style={{ textAlign: 'center', padding: '8px 4px', background: yld ? '#f0f9ff' : '#f9f9fb', borderRadius: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: yieldColor(yld) }}>{yld ? `${yld.toFixed(0)}%` : '—'}</div>
                <div style={{ fontSize: 9, color: '#636366', fontWeight: 600, textTransform: 'uppercase' }}>Yield</div>
              </div>
            </div>

            {/* Fill Progress */}
            <div style={{ padding: '0 16px 10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8e8e93', marginBottom: 3 }}>
                <span>{net > 0 ? `${net.toFixed(1)} ${comp.unit} net` : 'No picks yet'}</span>
                <span>{needed > 0 ? `${needed.toFixed(0)} ${comp.unit} needed` : ''}</span>
              </div>
              <div style={{ height: 5, background: '#f2f2f7', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${fillPct}%`, background: fillPct >= 100 ? '#34c759' : '#007aff', borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 6, padding: '6px 16px 14px', flexWrap: 'wrap' }}>
              <button onClick={() => openSheet('pick', comp)} style={actionBtnStyle('#34c759', '#e8f5e9')}>
                <ArrowDownToLine size={13} /> Pick
              </button>
              {hasPicks && (
                <>
                  <button onClick={() => openSheet('return', comp)} style={actionBtnStyle('#ff9500', '#fff3e0')}>
                    <RotateCcw size={13} /> Return
                  </button>
                  <button onClick={() => openSheet('waste', comp)} style={actionBtnStyle('#ff3b30', '#fce4ec')}>
                    <Trash2 size={13} /> Waste
                  </button>
                </>
              )}
              {logs.length > 0 && (
                <button onClick={() => openSheet('log', comp)} style={actionBtnStyle('#007aff', '#e8f0fe')}>
                  <FileText size={13} /> Log ({logs.length})
                </button>
              )}
            </div>

            {/* Expanded - Lots Detail */}
            {isExpanded && (
              <div style={{ borderTop: '1px solid #f2f2f7', padding: 16 }}>
                {(comp.lots || []).length > 0 ? (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1c1c1e', marginBottom: 8 }}>Lot Allocations</div>
                    {comp.lots.map((lot, li) => {
                      const lotPicked = (lot.picks || []).reduce((s, p) => s + (p.picked_qty || 0), 0);
                      const lotReturned = (lot.returns || []).reduce((s, r) => s + (r.returned_qty || 0), 0);
                      const lotWaste = (lot.waste || []).reduce((s, w) => s + (w.waste_qty || 0), 0);
                      return (
                        <div key={li} style={{ padding: '8px 0', borderBottom: li < comp.lots.length - 1 ? '1px solid #f2f2f7' : 'none' }}>
                          <div style={{ fontWeight: 600, fontSize: 14, color: '#1c1c1e' }}>{lot.lot_number}</div>
                          <div style={{ fontSize: 12, color: '#636366', marginTop: 2 }}>
                            {lot.allocated_qty > 0 && <span>Allocated: {lot.allocated_qty} · </span>}
                            <span style={{ color: '#34c759' }}>Picked: {lotPicked.toFixed(1)}</span>
                            {lotReturned > 0 && <span style={{ color: '#ff9500' }}> · Returned: {lotReturned.toFixed(1)}</span>}
                            {lotWaste > 0 && <span style={{ color: '#ff3b30' }}> · Waste: {lotWaste.toFixed(1)}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#8e8e93', fontSize: 13, padding: 10 }}>
                    No lots allocated yet. Use Pick to add inventory.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ====== SHEETS ====== */}

      {/* PICK SHEET - with LP scan */}
      {activeSheet === 'pick' && activeComponent && (
        <div className="mobile-sheet-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeSheet(); }}>
          <div className="mobile-sheet">
            <div className="mobile-sheet-handle" />
            <div className="mobile-sheet-title">
              <ArrowDownToLine size={18} style={{ display: 'inline', verticalAlign: -3, marginRight: 6, color: '#34c759' }} />
              Pick: {activeComponent.component_name}
            </div>
            <div style={{ fontSize: 13, color: '#8e8e93', marginBottom: 14 }}>
              Needed: ~{getNeeded(activeComponent).toFixed(0)} {activeComponent.unit} · Picked: {getPickedTotal(activeComponent).toFixed(1)} {activeComponent.unit}
            </div>

            {/* Scan LP Section */}
            <div style={{ background: '#f9f9fb', borderRadius: 12, padding: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#636366', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                <QrCode size={14} /> Scan or Enter LP
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="mobile-input" placeholder="LP-YYYYMMDD-XXXX" value={pickForm.lp_number} onChange={e => setPickForm(f => ({ ...f, lp_number: e.target.value }))} style={{ flex: 1, background: 'white' }} />
                <button className="mobile-action-btn primary" style={{ width: 'auto', padding: '10px 14px', fontSize: 13 }} onClick={lookupLP}>
                  <Search size={16} />
                </button>
              </div>
              {lpInfo && (
                <div style={{ marginTop: 8, padding: 8, background: '#e8f5e9', borderRadius: 8, fontSize: 12 }}>
                  <strong>{lpInfo.item_name}</strong> · Lot: {lpInfo.lot_number} · Qty: {lpInfo.quantity || lpInfo.quantity_on_hand} {lpInfo.uom || ''}
                </div>
              )}
            </div>

            {/* Manual Entry */}
            <div className="mobile-input-group">
              <label className="mobile-input-label">Lot Number</label>
              <input className="mobile-input" placeholder="LOT-XXXX-XXX" value={pickForm.lot_number} onChange={e => setPickForm(f => ({ ...f, lot_number: e.target.value }))} />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Quantity ({activeComponent.unit})</label>
              <input className="mobile-input" type="number" inputMode="decimal" placeholder="0" value={pickForm.quantity} onChange={e => setPickForm(f => ({ ...f, quantity: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="mobile-action-btn" style={{ background: '#f2f2f7', color: '#636366' }} onClick={closeSheet}>Cancel</button>
              <button className="mobile-action-btn success" onClick={handlePick} disabled={actionLoading}>
                {actionLoading ? 'Picking...' : 'Confirm Pick'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RETURN SHEET */}
      {activeSheet === 'return' && activeComponent && (
        <div className="mobile-sheet-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeSheet(); }}>
          <div className="mobile-sheet">
            <div className="mobile-sheet-handle" />
            <div className="mobile-sheet-title">
              <RotateCcw size={18} style={{ display: 'inline', verticalAlign: -3, marginRight: 6, color: '#ff9500' }} />
              Return: {activeComponent.component_name}
            </div>
            <div style={{ fontSize: 13, color: '#8e8e93', marginBottom: 14 }}>
              Picked: {getPickedTotal(activeComponent).toFixed(1)} · Returned: {getReturnedTotal(activeComponent).toFixed(1)} {activeComponent.unit}
            </div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Lot Number (from picks)</label>
              <input className="mobile-input" placeholder="LOT-XXXX-XXX" value={returnForm.lot_number} onChange={e => setReturnForm(f => ({ ...f, lot_number: e.target.value }))} />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Quantity ({activeComponent.unit})</label>
              <input className="mobile-input" type="number" inputMode="decimal" placeholder="0" value={returnForm.quantity} onChange={e => setReturnForm(f => ({ ...f, quantity: e.target.value }))} />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Reason</label>
              <select className="mobile-input" value={returnForm.reason} onChange={e => setReturnForm(f => ({ ...f, reason: e.target.value }))}>
                <option value="unused">Unused material</option>
                <option value="overissued">Over-issued</option>
                <option value="quality">Quality issue</option>
                <option value="wrong_item">Wrong item</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="mobile-action-btn" style={{ background: '#f2f2f7', color: '#636366' }} onClick={closeSheet}>Cancel</button>
              <button className="mobile-action-btn warning" onClick={handleReturn} disabled={actionLoading}>
                {actionLoading ? 'Returning...' : 'Confirm Return'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WASTE SHEET */}
      {activeSheet === 'waste' && activeComponent && (
        <div className="mobile-sheet-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeSheet(); }}>
          <div className="mobile-sheet">
            <div className="mobile-sheet-handle" />
            <div className="mobile-sheet-title">
              <Trash2 size={18} style={{ display: 'inline', verticalAlign: -3, marginRight: 6, color: '#ff3b30' }} />
              Log Waste: {activeComponent.component_name}
            </div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Lot Number (from picks)</label>
              <input className="mobile-input" placeholder="LOT-XXXX-XXX" value={wasteForm.lot_number} onChange={e => setWasteForm(f => ({ ...f, lot_number: e.target.value }))} />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Quantity ({activeComponent.unit})</label>
              <input className="mobile-input" type="number" inputMode="decimal" placeholder="0" value={wasteForm.quantity} onChange={e => setWasteForm(f => ({ ...f, quantity: e.target.value }))} />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Waste Reason</label>
              <select className="mobile-input" value={wasteForm.waste_reason} onChange={e => setWasteForm(f => ({ ...f, waste_reason: e.target.value }))}>
                <option value="floor_waste">Floor waste</option>
                <option value="poor_quality">Poor quality</option>
                <option value="contamination">Contamination</option>
                <option value="expired">Expired</option>
                <option value="equipment_failure">Equipment failure</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Notes</label>
              <input className="mobile-input" placeholder="Additional details" value={wasteForm.notes} onChange={e => setWasteForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="mobile-action-btn" style={{ background: '#f2f2f7', color: '#636366' }} onClick={closeSheet}>Cancel</button>
              <button className="mobile-action-btn danger" onClick={handleWaste} disabled={actionLoading}>
                {actionLoading ? 'Logging...' : 'Confirm Waste'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LOG SHEET */}
      {activeSheet === 'log' && activeComponent && (
        <div className="mobile-sheet-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeSheet(); }}>
          <div className="mobile-sheet">
            <div className="mobile-sheet-handle" />
            <div className="mobile-sheet-title">
              <FileText size={18} style={{ display: 'inline', verticalAlign: -3, marginRight: 6, color: '#007aff' }} />
              Activity Log: {activeComponent.component_name}
            </div>
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              {getAllLogs(activeComponent).length === 0 ? (
                <div style={{ textAlign: 'center', color: '#8e8e93', padding: 20 }}>No activity logged</div>
              ) : (
                getAllLogs(activeComponent).map((log, i) => (
                  <div key={i} className="mobile-activity-item">
                    <div className="mobile-activity-dot" style={{
                      background: log.type === 'pick' ? '#34c759' : log.type === 'return' ? '#ff9500' : '#ff3b30'
                    }} />
                    <div className="mobile-activity-content">
                      <div className="mobile-activity-text">
                        <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{log.type}</span>: {log.qty} {log.unit}
                        {log.reason ? ` (${log.reason})` : ''}
                      </div>
                      <div className="mobile-activity-time">
                        Lot: {log.lot} · {log.by || 'unknown'}
                        {log.at ? ` · ${format(new Date(log.at), 'MMM d, h:mm a')}` : ''}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <button className="mobile-action-btn" style={{ background: '#f2f2f7', color: '#636366', marginTop: 16 }} onClick={closeSheet}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

const actionBtnStyle = (color, bg) => ({
  display: 'flex', alignItems: 'center', gap: 5,
  padding: '7px 12px', borderRadius: 8,
  border: `1px solid ${color}`, background: bg, color: color,
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
});
